import axios from "axios";
import { createContext, useContext, useEffect } from "react";
import { useSupabaseAuth } from "contexts/SupabaseAuthContext";
import { supabase } from "services/supabase";
import moment from "moment";

const AxiosContext = createContext();

const API_URL = import.meta.env.VITE_API_URL;

// Create an instance of axios with a custom config
const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    "User-Timezone": Intl.DateTimeFormat().resolvedOptions().timeZone,
    "Current-Datetime": moment().toISOString(),
  },
});

const AxiosProvider = ({ children }) => {
  const { currentUser } = useSupabaseAuth();

  useEffect(() => {
    const interceptor = axiosInstance.interceptors.request.use(
      async (config) => {
        try {
          const {
            data: { session },
          } = await supabase.auth.getSession();

          if (session?.access_token) {
            config.headers["Authorization"] = `Bearer ${session.access_token}`;
          } else {
            delete config.headers["Authorization"];
          }

          window.axios = axiosInstance;
        } catch (error) {
          console.error("Failed to get auth token:", error);
          delete config.headers["Authorization"];
        }

        return config;
      },
      (error) => {
        console.error("Request interceptor error:", error.message);
        return Promise.reject(error);
      }
    );

    const responseInterceptor = axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401) {
          try {
            const {
              data: { session },
              error: refreshError,
            } = await supabase.auth.refreshSession();
            if (refreshError) {
              console.error("Token refresh failed:", refreshError);
            }
          } catch (refreshError) {
            console.error("Failed to refresh token:", refreshError);
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axiosInstance.interceptors.request.eject(interceptor);
      axiosInstance.interceptors.response.eject(responseInterceptor);
    };
  }, [currentUser]);

  return (
    <AxiosContext.Provider value={axiosInstance}>
      {children}
    </AxiosContext.Provider>
  );
};

const useAxios = () => {
  return useContext(AxiosContext);
};

export { axiosInstance, AxiosProvider, useAxios };
